/* eslint-disable react/no-unused-prop-types */
import * as React from 'react';
import classnames from 'classnames';
import { isObject } from 'lodash';
import { CompositeDecorator, Editor, EditorState, RichUtils } from 'draft-js';

import CharacterCount from 'components/character-count/CharacterCount';
import Tooltip from 'components/Tooltip';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';

import InlineStyleControls from './InlineStyleControls';
import BlockStyleControls from './BlockStyleControls';
import { getBlockStyle } from './StyleButton';
import RichTextEditorUrl from './RichTextEditorUrl';

import { getEditorState, toMarkdown, urlStrategy } from 'utils/editor';
import { expandDataProps } from 'utils/helper/common';

import './rich-text-editor.scss';

import type { Data } from 'types';

type Props = {
  canEdit?: boolean,
  liveUpdate?: boolean, // emit onChange immediately and not only on blur,
  value: any,
  placeholder?: string,
  charCount?: {
    maxChars: number
  },
  onChange?: (a: string) => void,
  onBlur?: (a: any) => void,
  data?: Data,
  onUpload?: (files: Array<File>) => void,
  supportedMimetypes?: Array<string>,
  uploadTooltipMessage?: string
};

type State = {
  editorState: any
};

class RichTextEditor extends React.PureComponent<Props, State> {
  editorRef: React.RefObject<any> = React.createRef();

  fileInput: HTMLInputElement | undefined | null;

  constructor(props: Props) {
    super(props);

    this.state = this.getDefaultState(this.props);
  }

  getDefaultState(nextProps: Props) {
    const editorState = getEditorState(nextProps.value);
    const compositeDecorator = new CompositeDecorator([
      {
        strategy: urlStrategy,
        component: RichTextEditorUrl
      }
    ]);
    const editorStateWithDecorators = EditorState.set(editorState, { decorator: compositeDecorator });

    return ({
      editorState: nextProps.canEdit ? editorState : editorStateWithDecorators
    });
  }

  focus = () => (
    this.editorRef.current && this.editorRef.current.focus()
  );

  handleBlur = (): void => {
    const { onChange, onBlur } = this.props;
    const markdown = toMarkdown(this.state.editorState);

    if (onBlur) {
      onBlur({
        currentTarget: {
          value: markdown
        }
      });
    }

    if (onChange) {
      onChange(markdown);
    }
  };

  handleLiveUpdateOnChange = () => {
    const { onChange, liveUpdate } = this.props;
    if (liveUpdate && onChange) {
      const markdown = toMarkdown(this.state.editorState);
      onChange(markdown);
    }
  };

  handleChange = (editorState: any): void => {
    this.setState({ editorState }, this.handleLiveUpdateOnChange);
  };

  handleKeyCommand = (command: string): boolean => {
    const { editorState } = this.state;
    const newState = RichUtils.handleKeyCommand(editorState, command);
    if (newState) {
      this.handleChange(newState);
      return true;
    }
    return false;
  };

  handleShowFileDialog = () => {
    if (this.fileInput) {
      this.fileInput.click();
    }
  };

  handleAttachFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { onUpload } = this.props;
    const { target } = event;
    const { files } = target;

    if (onUpload) {
      onUpload([...files ?? []]);
    }

    target.value = '';
  };

  toggleBlockType = (blockType: string): void => {
    this.handleChange(
      RichUtils.toggleBlockType(
        this.state.editorState,
        blockType
      )
    );
  };

  toggleInlineStyle = (inlineStyle: string): void => {
    this.handleChange(
      RichUtils.toggleInlineStyle(
        this.state.editorState,
        inlineStyle
      )
    );
  };

  renderCharacterCount(): React.ReactNode {
    const { charCount } = this.props;
    const { editorState } = this.state;
    const currentLength = editorState.getCurrentContent().getPlainText('').length;
    const maxChars = charCount ? charCount.maxChars : 0;

    return (
      <div className="rich-text-editor__char-count">
        <CharacterCount
          charCount={currentLength}
          charLimit={maxChars}
          align="right"
          size="normal"
          highlightOnError
        />
      </div>
    );
  }

  render(): React.ReactNode {
    const { canEdit, placeholder, charCount, data, supportedMimetypes, onUpload, uploadTooltipMessage } = this.props;
    const { editorState } = this.state;

    const containerClasses = classnames({
      'rich-text-editor__container': true,
      'rich-text-editor__container--can-edit': canEdit
    });

    const editorClasses = classnames({
      'rich-text-editor__editor': true,
      'rich-text-editor__editor--can-edit': canEdit
    });

    return (
      <section className="rich-text-editor">
        <div className={containerClasses}>
          {
            canEdit &&
              <div className="rich-text-editor__toolbar">
                <InlineStyleControls
                  editorState={editorState}
                  onToggle={this.toggleInlineStyle}
                />
                <BlockStyleControls
                  editorState={editorState}
                  onToggle={this.toggleBlockType}
                />
                { onUpload && (
                  <div className="rich-text-editor__upload">
                    <Tooltip
                      message={uploadTooltipMessage}
                      trigger="hover"
                      width="auto-compact"
                      forceHide={!uploadTooltipMessage}
                    >
                      <SVGDynamicIcon
                        name="upload-custom-fg"
                        width={22}
                        height={22}
                        clickable
                        customColor="grey"
                        onClick={this.handleShowFileDialog}
                      />
                      <input
                        ref={(ref) => { this.fileInput = ref; }}
                        className="messenger-actions__file"
                        onChange={this.handleAttachFile}
                        type="file"
                        accept={(supportedMimetypes || []).join(',')}
                        multiple
                      />
                    </Tooltip>
                  </div>
                )}
              </div>
          }
          <div
            className={editorClasses}
            onClick={this.focus}
            data-hj-suppress
            {...expandDataProps(data)}
          >
            <Editor
              blockStyleFn={getBlockStyle}
              editorState={editorState}
              handleKeyCommand={this.handleKeyCommand}
              onChange={this.handleChange}
              onBlur={this.handleBlur}
              readOnly={!canEdit}
              ref={this.editorRef}
              placeholder={placeholder}
            />
          </div>
        </div>
        {
          isObject(charCount) &&
            this.renderCharacterCount()
        }
      </section>
    );
  }
}

export default RichTextEditor;
